import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Link2,
  Unlink,
  RefreshCw,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  ArrowLeft,
  Inbox,
  Zap,
  Send,
  Building2,
  Briefcase,
  Info
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format, isValid, parseISO } from "date-fns";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const formatDate = (dateString, pattern = 'MMM d, yyyy h:mm a') => {
  if (!dateString) return 'Never';
  try {
    const date = parseISO(dateString);
    if (isValid(date)) {
      return format(date, pattern);
    }
    return 'Never';
  } catch {
    return 'Never';
  }
};

const EMAIL_TYPE_LABELS = {
  APPLICATION_CONFIRMATION: { label: "Application Received", color: "#A3C3D8", icon: CheckCircle },
  INTERVIEW_INVITE: { label: "Interview Invite", color: "#F6AD55", icon: Mail },
  OFFER_LETTER: { label: "Offer Letter", color: "#68D391", icon: Zap },
  REJECTION: { label: "Rejection", color: "#FC8181", icon: AlertCircle },
  GENERIC_FOLLOWUP: { label: "Follow-up", color: "#94A3B8", icon: Inbox }
};

export default function EmailTracking() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [emailEvents, setEmailEvents] = useState([]);
  const [showSimulator, setShowSimulator] = useState(false);
  const [simulatorData, setSimulatorData] = useState({
    email_type: "APPLICATION_CONFIRMATION",
    company: "",
    job_title: ""
  });
  const [simulating, setSimulating] = useState(false);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    fetchConnectionStatus();
    fetchEmailEvents();
    fetchQuota();
    fetchSyncStatus();
    
    // Poll for status updates every minute to show updated sync times
    const interval = setInterval(() => {
      if (connectionStatus?.connected) {
        fetchConnectionStatus();
        fetchEmailEvents();
        fetchSyncStatus();
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [connectionStatus?.connected]);

  const fetchConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/email/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch email status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/email/sync-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch sync status:", error);
    }
  };

  const fetchEmailEvents = async () => {
    try {
      const response = await axios.get(`${API}/email/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailEvents(response.data);
    } catch (error) {
      console.error("Failed to fetch email events:", error);
    }
  };

  const fetchQuota = async () => {
    try {
      const response = await axios.get(`${API}/payments/quota`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuota(response.data);
    } catch (error) {
      console.error("Failed to fetch quota:", error);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/email/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      // Redirect to Google OAuth in the same window
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error("Failed to initiate Gmail connection");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect your email?")) return;
    
    try {
      await axios.delete(`${API}/email/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus({ connected: false });
      toast.success("Email disconnected");
    } catch (error) {
      toast.error("Failed to disconnect email");
    }
  };

  const handleSync = async () => {
    // Check if limit reached before attempting sync
    if (syncStatus && syncStatus.syncs_remaining <= 0) {
      toast.error("Daily sync limit reached. Please try again tomorrow.");
      return;
    }
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/email/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update sync status from response
      if (response.data.syncs_remaining !== undefined) {
        setSyncStatus({
          syncs_used: response.data.syncs_used,
          syncs_remaining: response.data.syncs_remaining,
          max_syncs_per_day: response.data.max_syncs_per_day
        });
      }
      
      toast.success(`Synced ${response.data.processed} emails, ${response.data.matched} matched`);
      fetchConnectionStatus();
      fetchEmailEvents();
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Daily sync limit reached. Please try again tomorrow.");
        fetchSyncStatus();
      } else {
        toast.error(error.response?.data?.detail || "Failed to sync emails");
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleSimulate = async () => {
    if (!simulatorData.company) {
      toast.error("Please enter a company name");
      return;
    }

    setSimulating(true);
    try {
      const response = await axios.post(
        `${API}/email/simulate`,
        null,
        {
          params: simulatorData,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.matched) {
        toast.success(`Email matched and status updated!`);
      } else {
        toast.info("Email simulated but couldn't match to an application");
      }
      
      fetchEmailEvents();
      setSimulatorData({ email_type: "APPLICATION_CONFIRMATION", company: "", job_title: "" });
    } catch (error) {
      toast.error("Failed to simulate email");
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F1EC]">
        <Navbar token={token} quota={quota} onQuotaUpdate={setQuota} />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-[#A3C3D8]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F1EC]" data-testid="email-tracking-page">
      <Navbar token={token} quota={quota} onQuotaUpdate={setQuota} />
      
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="text-[#555F67]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-[#2D3748]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Email Tracking
            </h1>
            <p className="text-sm text-[#94A3B8]">
              Connect your email to auto-track application status changes
            </p>
          </div>
        </div>

        {/* Connection Status Card */}
        <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm mb-8">
          <h2 className="text-lg font-semibold text-[#2D3748] mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Mail className="h-5 w-5 text-[#A3C3D8]" />
            Email Connection
          </h2>

          {connectionStatus?.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-800">Connected</p>
                  <p className="text-sm text-green-700">{connectionStatus.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[#94A3B8]">Connected Since</p>
                  <p className="font-medium text-[#2D3748]">
                    {formatDate(connectionStatus.connected_at)}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[#94A3B8]">Last Sync</p>
                  <p className="font-medium text-[#2D3748]">
                    {formatDate(connectionStatus.last_sync)}
                  </p>
                </div>
              </div>

              {/* Sync Limit Info */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-blue-800">Manual Sync Only</p>
                  <p className="text-sm text-blue-700">
                    {syncStatus ? (
                      syncStatus.syncs_remaining > 0 ? (
                        <>You have <strong>{syncStatus.syncs_remaining}</strong> of {syncStatus.max_syncs_per_day} syncs remaining today</>
                      ) : (
                        <span className="text-red-600 font-medium">Daily sync limit reached. Please try again tomorrow.</span>
                      )
                    ) : (
                      <>Only {3} manual syncs allowed per day</>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSync}
                  disabled={syncing || (syncStatus && syncStatus.syncs_remaining <= 0)}
                  className={`${
                    syncStatus && syncStatus.syncs_remaining <= 0 
                      ? "bg-gray-300 cursor-not-allowed" 
                      : "bg-[#A3C3D8] hover:bg-[#8FB3C9]"
                  } text-[#152A38]`}
                  data-testid="sync-emails-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {syncStatus && syncStatus.syncs_remaining <= 0 ? "Limit Reached" : "Sync Now"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDisconnect}
                  className="text-red-500 border-red-200 hover:bg-red-50"
                  data-testid="disconnect-email-btn"
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <AlertCircle className="h-5 w-5 text-[#94A3B8]" />
                <div>
                  <p className="font-medium text-[#555F67]">Not Connected</p>
                  <p className="text-sm text-[#94A3B8]">Connect your Gmail to enable auto-tracking</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-800 mb-2">How it works:</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• We monitor for hiring-related emails only</li>
                  <li>• Auto-detect application confirmations, interview invites, offers, and rejections</li>
                  <li>• Automatically update your application status</li>
                  <li>• Read-only access - we never send emails</li>
                </ul>
              </div>

              <Button
                onClick={handleConnect}
                disabled={connecting}
                className="w-full bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38] h-12"
                data-testid="connect-email-btn"
              >
                {connecting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Link2 className="h-4 w-4 mr-2" />
                )}
                Connect Gmail
              </Button>
            </div>
          )}
        </div>

        {/* Email Simulator (Demo) */}
        <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#2D3748] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Send className="h-5 w-5 text-[#A3C3D8]" />
              Email Simulator (Demo)
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSimulator(!showSimulator)}
            >
              {showSimulator ? "Hide" : "Show"}
            </Button>
          </div>

          {showSimulator && (
            <div className="space-y-4 pt-4 border-t">
              <p className="text-sm text-[#94A3B8]">
                Simulate receiving a hiring email to test auto-status updates
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#555F67]">Email Type</Label>
                  <Select 
                    value={simulatorData.email_type} 
                    onValueChange={(value) => setSimulatorData({ ...simulatorData, email_type: value })}
                  >
                    <SelectTrigger data-testid="email-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="APPLICATION_CONFIRMATION">Application Confirmation</SelectItem>
                      <SelectItem value="INTERVIEW_INVITE">Interview Invite</SelectItem>
                      <SelectItem value="OFFER_LETTER">Offer Letter</SelectItem>
                      <SelectItem value="REJECTION">Rejection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#555F67] flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Company Name
                  </Label>
                  <Input
                    value={simulatorData.company}
                    onChange={(e) => setSimulatorData({ ...simulatorData, company: e.target.value })}
                    placeholder="e.g., Google"
                    className="border-slate-200"
                    data-testid="simulator-company-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#555F67] flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Job Title (Optional)
                  </Label>
                  <Input
                    value={simulatorData.job_title}
                    onChange={(e) => setSimulatorData({ ...simulatorData, job_title: e.target.value })}
                    placeholder="e.g., Software Engineer"
                    className="border-slate-200"
                    data-testid="simulator-title-input"
                  />
                </div>
              </div>

              <Button
                onClick={handleSimulate}
                disabled={simulating || !simulatorData.company}
                className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
                data-testid="simulate-email-btn"
              >
                {simulating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Simulate Email
              </Button>
            </div>
          )}
        </div>

        {/* Email Events List */}
        <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-lg font-semibold text-[#2D3748] mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Inbox className="h-5 w-5 text-[#A3C3D8]" />
            Recent Email Events
          </h2>

          {emailEvents.length === 0 ? (
            <div className="text-center py-12 text-[#94A3B8]">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No email events yet</p>
              <p className="text-sm">Connect your email or use the simulator to test</p>
            </div>
          ) : (
            <div className="space-y-3">
              {emailEvents.map((event) => {
                const typeInfo = EMAIL_TYPE_LABELS[event.email_type] || EMAIL_TYPE_LABELS.GENERIC_FOLLOWUP;
                const TypeIcon = typeInfo.icon;
                
                return (
                  <div 
                    key={event.id}
                    className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg"
                    data-testid={`email-event-${event.id}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${typeInfo.color}20` }}
                    >
                      <TypeIcon className="h-5 w-5" style={{ color: typeInfo.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span 
                          className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ 
                            backgroundColor: `${typeInfo.color}20`,
                            color: typeInfo.color
                          }}
                        >
                          {typeInfo.label}
                        </span>
                        {event.matched && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            Matched
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-[#2D3748] truncate">{event.subject}</p>
                      <p className="text-sm text-[#94A3B8]">From: {event.sender}</p>
                      <p className="text-xs text-[#94A3B8] mt-1">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {formatDate(event.received_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
